import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ChevronLeft, Minus, Plus, ShoppingCart, Package, Ticket, Check } from "lucide-react";
import { useCart, formatPrice } from "@/lib/cart";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    setLoading(true);
    api.get(`/shop/products/${id}`)
      .then(({ data }) => {
        setProduct(data);
        if (data.variations && data.variations.length > 0) {
          setSelectedVariation(data.variations[0]);
        }
      })
      .catch(() => setError("Product not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const handleAddToCart = () => {
    if (!product || !selectedVariation) return;
    addItem(product, selectedVariation, quantity);
    setAdded(true);
    toast.success(`${product.name} added to cart`);
    setTimeout(() => setAdded(false), 2000);
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <Skeleton className="h-8 w-32 mb-6" />
          <div className="grid md:grid-cols-2 gap-8">
            <Skeleton className="aspect-square rounded-xl" />
            <div className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-12 w-full mt-6" />
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (error || !product) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 text-center">
          <Package className="h-12 w-12 mx-auto text-[hsl(var(--muted-fg))] mb-4" />
          <p className="text-lg font-medium">{error || "Product not found"}</p>
          <Link to="/shop">
            <Button variant="outline" className="mt-4" data-testid="product-back-to-shop">
              Back to Shop
            </Button>
          </Link>
        </div>
      </PublicLayout>
    );
  }

  const hasImage = product.image_urls && product.image_urls.length > 0;
  const currency = selectedVariation?.currency || "AUD";

  return (
    <PublicLayout>
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Back link */}
        <Link
          to="/shop"
          className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-fg))] hover:text-[hsl(var(--fg))] transition-colors mb-6"
          data-testid="product-back-link"
        >
          <ChevronLeft className="h-4 w-4" /> Back to Shop
        </Link>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Image */}
          <div className="relative aspect-square rounded-xl overflow-hidden bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]">
            {hasImage ? (
              <img
                src={product.image_urls[0]}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-image"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                {product.category === "tickets" ? (
                  <Ticket className="h-16 w-16 text-[hsl(var(--muted-fg))]" />
                ) : (
                  <Package className="h-16 w-16 text-[hsl(var(--muted-fg))]" />
                )}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-col">
            <Badge
              className="w-fit mb-3 text-xs"
              variant={product.category === "tickets" ? "default" : "secondary"}
              data-testid="product-category-badge"
            >
              {product.category === "tickets" ? "Ticket / Event" : "Merchandise"}
            </Badge>

            <h1
              className="text-2xl sm:text-3xl font-semibold text-[hsl(var(--fg))] tracking-tight"
              data-testid="product-name"
            >
              {product.name}
            </h1>

            {product.description && (
              <p className="mt-3 text-sm text-[hsl(var(--muted-fg))] leading-relaxed" data-testid="product-description">
                {product.description}
              </p>
            )}

            {/* Price */}
            <div className="mt-4" data-testid="product-price">
              <span className="text-2xl font-bold text-[hsl(var(--fg))]">
                {selectedVariation ? formatPrice(selectedVariation.price_cents, currency) : "—"}
              </span>
            </div>

            {/* Variants */}
            {product.variations && product.variations.length > 1 && (
              <div className="mt-6">
                <Label className="text-sm font-medium text-[hsl(var(--fg))] mb-2 block">Options</Label>
                <RadioGroup
                  value={selectedVariation?.id || ""}
                  onValueChange={(val) => {
                    const v = product.variations.find((x) => x.id === val);
                    if (v) setSelectedVariation(v);
                  }}
                  className="flex flex-wrap gap-2"
                  data-testid="product-variant-selector"
                >
                  {product.variations.map((v) => (
                    <div key={v.id} className="flex items-center">
                      <RadioGroupItem value={v.id} id={`var-${v.id}`} className="sr-only" />
                      <Label
                        htmlFor={`var-${v.id}`}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium cursor-pointer transition-colors ${
                          selectedVariation?.id === v.id
                            ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))]"
                            : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"
                        }`}
                        data-testid={`product-variant-${v.id}`}
                      >
                        {v.name}
                        {v.price_cents !== selectedVariation?.price_cents && (
                          <span className="ml-1 opacity-70">({formatPrice(v.price_cents, currency)})</span>
                        )}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Quantity */}
            <div className="mt-6">
              <Label className="text-sm font-medium text-[hsl(var(--fg))] mb-2 block">Quantity</Label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  data-testid="product-qty-minus"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="text-lg font-semibold w-8 text-center" data-testid="product-qty-display">
                  {quantity}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10"
                  onClick={() => setQuantity((q) => Math.min(99, q + 1))}
                  disabled={quantity >= 99}
                  data-testid="product-qty-plus"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Add to Cart */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                className={`flex-1 min-h-[48px] text-base font-semibold gap-2 transition-colors duration-200 ${
                  added
                    ? "bg-[hsl(142,42%,34%)] hover:bg-[hsl(142,42%,34%)]/90"
                    : "bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
                } text-[hsl(var(--primary-fg))]"`}
                onClick={handleAddToCart}
                disabled={!selectedVariation}
                data-testid="shop-add-to-cart-button"
              >
                {added ? (
                  <><Check className="h-5 w-5" /> Added!</>
                ) : (
                  <><ShoppingCart className="h-5 w-5" /> Add to Cart</>
                )}
              </Button>
              <Button
                variant="outline"
                className="min-h-[48px] gap-2 border-[hsl(var(--border))]"
                onClick={() => {
                  if (selectedVariation) {
                    addItem(product, selectedVariation, quantity);
                    nav("/shop/cart");
                  }
                }}
                disabled={!selectedVariation}
                data-testid="product-buy-now-button"
              >
                Buy Now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
